import './process-list.css'

import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { PageHeader, Spinner } from '../../../Components'
import { useAuth } from '../../../Provider/AuthProvider'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { getProcessStatusVariant } from '../../../utils/statusVariants'
import { GigiChatMessages } from '../../Gigi/GigiForm/GigiChatMessages'
import { STATE_ENUM } from '../../Gigi/GigiList/Functions'
import { columnProps } from '../../Prompts/Functions'
import { handleContinueClick } from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Pagina lista processi di una conversazione con pannello chat affiancato.
 * @returns {JSX.Element} Componente lista processi
 */
export function ProcessList() {
  const {
    user: { user }
  } = useAuth()
  const { conversationId } = useParams()
  const { data: conversation = [], isSuccess } = useCQuery(['conversation', conversationId])
  const [processesTable, setProcessesTable] = useState([])
  const [messagesList, setMessagesList] = useState([])
  const [showContinueBtn, setShowContinueBtn] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (isSuccess) {
      setMessagesList(
        conversation?.messages
          ?.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt))
          .map((message) => ({
            message: message.message,
            user: message.sender === 'traveller' ? conversation?.traveller?.email : 'Nora',
            createdAt: new Date(message.createdAt)
          }))
      )
      setShowContinueBtn(!!conversation?.processes?.find(({ state }) => state === 5))
      setProcessesTable(
        conversation?.processes?.map((r) => ({
          ...r,
          stateValue: r.state,
          stateLabel: STATE_ENUM?.find((e) => e.value === r.state)?.label,
          createdAt: new Date(r.createdAt).toLocaleString()
        }))
      )
    }
  }, [isSuccess, conversation])

  if (!isSuccess) return <Spinner />

  const travellerName = [conversation?.traveller?.name, conversation?.traveller?.surname]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="process-list-page">
      <PageHeader
        title={t.processListTitle}
        subtitle={`${t.conversation} #${conversationId}${travellerName ? ` · ${travellerName}` : ''}`}
        actions={
          <>
            <button type="button" className="nbtn" onClick={() => navigate('/conversations/list')}>
              <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
              {t.tornaIndietro}
            </button>
            {user?.role === 'admin' && (
              <>
                <button
                  type="button"
                  className="nbtn"
                  onClick={() => navigate('/operatore/' + conversationId)}
                >
                  <i className="pi pi-cog" style={{ fontSize: 14 }} />
                  {t.goToForm}
                </button>
                <button
                  type="button"
                  className="nbtn"
                  onClick={() => navigate('/pricing/' + conversationId)}
                >
                  <i className="pi pi-chart-pie" style={{ fontSize: 14 }} />
                  {t.pricing}
                </button>
              </>
            )}
          </>
        }
      />

      <div className="process-list-grid">
        <div className="ncard process-list-table-wrap" style={{ padding: 0 }}>
          <div className="ncard__head">
            <div className="ncard__ico">
              <i className="pi pi-bolt" style={{ fontSize: 16 }} />
            </div>
            <div>
              <h3 className="ncard__title">{t.processListTitle}</h3>
            </div>
          </div>
          <DataTable
            style={{ width: '100%' }}
            filterDisplay="row"
            sortField="createdAt"
            sortOrder={-1}
            className="TableSignatures ntable-datatable"
            value={processesTable}
            emptyMessage={t.noProcessFound}
            responsiveLayout="scroll"
            paginator
            rows={10}
          >
            <Column
              field="id"
              {...columnProps(t.processId)}
              body={({ id }) => <span className="ntable__num">#{id}</span>}
            />
            <Column
              field="stateLabel"
              {...columnProps(t.state)}
              body={({ stateValue, stateLabel }) => (
                <span className={`nora-status nora-status--${getProcessStatusVariant(stateValue)}`}>
                  <span className="nora-status__dot" />
                  <span className="nora-status__label">{stateLabel}</span>
                </span>
              )}
            />
            <Column
              field="createdAt"
              {...columnProps(t.creationDate)}
              body={({ createdAt }) => (
                <span className="ntable__meta" style={{ fontFamily: 'var(--font-mono)' }}>
                  {createdAt}
                </span>
              )}
            />
            <Column
              headerStyle={{ visibility: 'hidden' }}
              style={{ border: 'none', width: 50, textAlign: 'center' }}
              body={({ id }) => (
                <button
                  type="button"
                  className="naction-btn naction-btn--primary"
                  title={t.openStep}
                  onClick={() => navigate('/process/' + id)}
                >
                  <i className="pi pi-bolt" style={{ fontSize: 14 }} />
                </button>
              )}
            />
            <Column
              headerStyle={{ visibility: 'hidden' }}
              style={{ border: 'none', width: 50, textAlign: 'center' }}
              body={({ id }) => (
                <button
                  type="button"
                  className="naction-btn"
                  title={t.statistics}
                  onClick={() => navigate('/process/stats/' + id)}
                >
                  <i className="pi pi-chart-bar" style={{ fontSize: 14 }} />
                </button>
              )}
            />
          </DataTable>
        </div>

        <div className="ncard process-list-chat">
          <div className="ncard__head">
            <div className="ncard__ico">
              <i className="pi pi-comments" style={{ fontSize: 16 }} />
            </div>
            <div>
              <h3 className="ncard__title">{t.conversation}</h3>
              <div className="ncard__sub">
                {messagesList.length} {t.messages} · ID #{conversationId}
              </div>
            </div>
            <div className="ncard__head-actions">
              <span className="nbadge">
                <i className="pi pi-eye" style={{ fontSize: 11 }} /> {t.readOnly}
              </span>
            </div>
          </div>
          <div className="ncard__body process-list-chat__body">
            <GigiChatMessages messages={messagesList} />
          </div>
          {showContinueBtn && (
            <div className="process-list-chat__footer">
              <button
                type="button"
                className="nbtn nbtn--primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => handleContinueClick(conversation)}
              >
                <i className="pi pi-play" style={{ fontSize: 14 }} />
                {t.continueExecution}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
